import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, MapPin, CheckCircle, AlertCircle, RotateCcw, Vibrate, Scan } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const MobileSFGPutAway = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const binInputRef = useRef(null);
  const usnInputRef = useRef(null);

  // States
  const [step, setStep] = useState('staff'); // staff, scan, usn_scan, confirm, success
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}] multi-staff team
  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [binCode, setBinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [putAwayCount, setPutAwayCount] = useState(0);
  
  // Re-put-away (combo return) USN scanning state
  const [isRePutaway, setIsRePutaway] = useState(false);
  const [originalUsnCodes, setOriginalUsnCodes] = useState([]);
  const [scannedUsns, setScannedUsns] = useState([]);
  const [usnInput, setUsnInput] = useState('');

  useEffect(() => {
    if (step === 'staff') {
      setTimeout(() => staffInputRef.current?.focus(), 100);
    } else if (step === 'scan') {
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } else if (step === 'usn_scan') {
      setTimeout(() => usnInputRef.current?.focus(), 100);
    } else if (step === 'confirm') {
      setTimeout(() => binInputRef.current?.focus(), 100);
    }
  }, [step]);

  const vibrate = (pattern = [50]) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  const handleStaffValidate = async () => {
    const id = staffId.trim();
    if (!id) {
      toast.error('Please scan Staff ID');
      vibrate([100, 50, 100]);
      return;
    }
    if (staffList.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffId('');
      setTimeout(() => staffInputRef.current?.focus(), 100);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${id}`);
      if (response.data.valid) {
        const nm = response.data.staff_name;
        setStaffList((prev) => [...prev, { staff_id: id, staff_name: nm }]);
        setStaffId('');
        vibrate([50]);
        toast.success(`Added ${nm}`);
        setTimeout(() => staffInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
        vibrate([100, 50, 100]);
      }
    } catch (error) {
      toast.error('Staff validation failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStaff = (id) => {
    setStaffList((prev) => prev.filter((s) => s.staff_id !== id));
  };

  const handleStartSession = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      vibrate([100, 50, 100]);
      return;
    }
    setStaffName(staffList.map((s) => s.staff_name).join(', '));
    setStep('scan');
    vibrate([50]);
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan bundle barcode');
      vibrate([100, 50, 100]);
      return;
    }

    try {
      setLoading(true);
      // Pass staff_id for location locking to prevent traffic congestion
      const response = await apiClient.post(`/wms/sfg/validate-bundle/${bundleCode}?staff_id=${encodeURIComponent(staffList[0]?.staff_id || '')}`);
      
      setBundleDetails(response.data.bundle);
      if (response.data.suggested_bin) {
        setSuggestedBin(response.data.suggested_bin);
      }
      
      // Keep bin location input blank - user must manually enter/scan
      setBinCode('');
      
      // Check if this is a re-put-away (combo return) requiring USN scanning
      if (response.data.is_re_putaway && response.data.original_usn_codes?.length > 0) {
        setIsRePutaway(true);
        setOriginalUsnCodes(response.data.original_usn_codes);
        
        if (response.data.skip_usn_scan) {
          // Full-bundle return (e.g., "Return to SFG" from dispatch) — USNs intact, skip to bin assignment
          setScannedUsns(response.data.original_usn_codes);
          setStep('confirm');
          vibrate([50]);
          toast.success(`Full-bundle return — ${response.data.original_usn_codes.length} USNs. Scan bin to put away.`);
        } else {
          // Combo Component return bundle — USNs may include substitutes, require re-scan
          setScannedUsns([]);
          setUsnInput('');
          setStep('usn_scan');
          vibrate([50]);
          toast.success(`Combo return bundle — scan each USN (${response.data.original_usn_codes.length} in bundle)`);
        }
      } else {
        setIsRePutaway(false);
        setStep('confirm');
        vibrate([50]);
        toast.success('Bundle validated!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle not found');
      vibrate([100, 50, 100]);
      setBundleCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleUsnScan = () => {
    const usn = usnInput.trim().toUpperCase();
    if (!usn) return;
    
    if (!originalUsnCodes.includes(usn)) {
      toast.error(`USN ${usn} does not belong to this bundle`);
      vibrate([100, 50, 100]);
      setUsnInput('');
      return;
    }
    
    if (scannedUsns.includes(usn)) {
      toast.error(`USN ${usn} already scanned`);
      vibrate([100, 50, 100]);
      setUsnInput('');
      return;
    }
    
    setScannedUsns(prev => [...prev, usn]);
    setUsnInput('');
    vibrate([50]);
    toast.success(`USN ${usn} scanned (${scannedUsns.length + 1}/${originalUsnCodes.length})`);
    setTimeout(() => usnInputRef.current?.focus(), 100);
  };

  const handleUsnScanDone = () => {
    if (scannedUsns.length === 0) {
      toast.error('Scan at least one USN before proceeding');
      vibrate([100, 50, 100]);
      return;
    }
    setStep('confirm');
  };

  const handleConfirmPutAway = async () => {
    if (!binCode.trim()) {
      toast.error('Please enter bin location');
      vibrate([100, 50, 100]);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        staff_id: staffList[0]?.staff_id || '',
        staff_ids: staffList.map((s) => s.staff_id),
        bundle_code: bundleCode,
        bin_location: binCode
      };
      
      // For combo returns, include only scanned USNs
      if (isRePutaway && scannedUsns.length > 0) {
        payload.scanned_usns = scannedUsns;
      }
      
      const response = await apiClient.post('/wms/sfg/put-away', payload);

      setPutAwayCount(prev => prev + 1);
      setStep('success');
      vibrate([50, 100, 50]);
      
      if (isRePutaway) {
        const consumed = originalUsnCodes.length - scannedUsns.length;
        toast.success(`Put-away complete! ${scannedUsns.length} returned, ${consumed} consumed`);
      } else {
        toast.success('Put-away complete!');
      }
      
      // Auto-reset after 2 seconds
      setTimeout(() => {
        resetForNextBundle();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Put-away failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const resetForNextBundle = () => {
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setBinCode('');
    setIsRePutaway(false);
    setOriginalUsnCodes([]);
    setScannedUsns([]);
    setUsnInput('');
    setStep('scan');
  };

  const resetAll = () => {
    setStaffId('');
    setStaffName('');
    setStaffList([]);
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setBinCode('');
    setPutAwayCount(0);
    setIsRePutaway(false);
    setOriginalUsnCodes([]);
    setScannedUsns([]);
    setUsnInput('');
    setStep('staff');
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/wms/mobile')}
            className="text-white hover:bg-white/20 -ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold">SFG Put-Away</h1>
            {staffName && (
              <p className="text-sm text-purple-200">{staffName}</p>
            )}
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
        
        {/* Progress indicator */}
        <div className="mt-4 flex items-center gap-2">
          {['staff', 'scan', ...(isRePutaway ? ['usn_scan'] : []), 'confirm', 'success'].map((s, i, arr) => {
            const currentIdx = arr.indexOf(step);
            return (
              <div key={s} className={`flex-1 h-2 rounded-full ${i <= currentIdx ? 'bg-white' : 'bg-white/30'}`} />
            );
          })}
        </div>
      </header>

      {/* Session Stats */}
      {step !== 'staff' && (
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-gray-400">Session Put-Aways:</span>
          <span className="font-bold text-green-400">{putAwayCount}</span>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 p-4 flex flex-col">
        {/* Step: Staff Validation (multi-staff) */}
        {step === 'staff' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-5">
              <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="w-10 h-10 text-purple-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Staff ID</h2>
                <p className="text-gray-400">Add one or more staff sharing this put-away</p>
              </div>
              <Input
                ref={staffInputRef}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Staff ID"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-staff-input"
              />
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="w-full h-14 text-lg font-bold bg-purple-600 hover:bg-purple-700 rounded-xl"
                data-testid="mobile-staff-add-btn"
              >
                {loading ? 'Validating...' : '+ Add Staff'}
              </Button>

              {staffList.length > 0 && (
                <div className="space-y-2" data-testid="mobile-staff-list">
                  <p className="text-xs text-gray-400 font-medium">{staffList.length} staff added:</p>
                  <div className="flex flex-wrap gap-2">
                    {staffList.map((s) => (
                      <div
                        key={s.staff_id}
                        className="flex items-center gap-2 bg-purple-500/20 border border-purple-500/40 rounded-full pl-3 pr-1 py-1"
                        data-testid={`mobile-staff-chip-${s.staff_id}`}
                      >
                        <span className="text-sm text-white">{s.staff_name}</span>
                        <span className="text-xs text-purple-300">{s.staff_id}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveStaff(s.staff_id)}
                          className="w-6 h-6 rounded-full bg-purple-600/60 hover:bg-red-600 flex items-center justify-center text-white text-xs"
                          data-testid={`mobile-staff-remove-${s.staff_id}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleStartSession}
                disabled={loading || staffList.length === 0}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-staff-continue-btn"
              >
                Continue ({staffList.length})
              </Button>
            </div>
          </div>
        )}

        {/* Step: Bundle Scan */}
        {step === 'scan' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <Package className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Bundle</h2>
                <p className="text-gray-400">Scan the bundle barcode</p>
              </div>
              <Input
                ref={bundleInputRef}
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                placeholder="Bundle Barcode"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-bundle-input"
              />
              <Button
                onClick={handleBundleScan}
                disabled={loading || !bundleCode}
                className="w-full h-16 text-xl font-bold bg-blue-600 hover:bg-blue-700 rounded-xl"
              >
                {loading ? 'Validating...' : 'Validate Bundle'}
              </Button>
            </div>
          </div>
        )}

        {/* Step: USN Scan (Combo Return) */}
        {step === 'usn_scan' && bundleDetails && (
          <div className="flex-1 flex flex-col space-y-4">
            {/* Header */}
            <div className="bg-amber-900/30 border border-amber-700/50 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Scan className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-amber-100">Scan Returned USNs</h2>
                  <p className="text-amber-300 text-xs">Bundle: {bundleCode} ({bundleDetails.fg_name || bundleDetails.sku_code})</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-amber-300 text-sm">Scanned: {scannedUsns.length} / {originalUsnCodes.length}</span>
                <Badge variant="outline" className="bg-amber-900/50 text-amber-200 border-amber-600">
                  {originalUsnCodes.length - scannedUsns.length} remaining
                </Badge>
              </div>
              <Progress 
                value={(scannedUsns.length / originalUsnCodes.length) * 100} 
                className="mt-2 h-2 bg-amber-900/50" 
              />
            </div>

            {/* USN Scanner */}
            <div className="bg-gray-800 rounded-2xl p-4 space-y-3">
              <Input
                ref={usnInputRef}
                value={usnInput}
                onChange={(e) => setUsnInput(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') handleUsnScan(); }}
                placeholder="Scan USN barcode..."
                className="h-14 text-lg text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="usn-scan-input"
                autoFocus
              />
              <Button
                onClick={handleUsnScan}
                disabled={!usnInput.trim()}
                className="w-full h-12 text-lg font-bold bg-amber-600 hover:bg-amber-700 rounded-xl"
                data-testid="usn-scan-btn"
              >
                Scan USN
              </Button>
            </div>

            {/* Scanned USN list */}
            {scannedUsns.length > 0 && (
              <div className="bg-gray-800 rounded-2xl p-4 max-h-40 overflow-y-auto">
                <p className="text-gray-400 text-xs mb-2">Scanned USNs:</p>
                <div className="flex flex-wrap gap-1">
                  {scannedUsns.map((usn, i) => (
                    <span key={usn} className="inline-block bg-green-900/40 text-green-300 text-xs font-mono px-2 py-1 rounded">
                      {usn}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Done button */}
            <Button
              onClick={handleUsnScanDone}
              disabled={scannedUsns.length === 0}
              className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 rounded-xl"
              data-testid="usn-scan-done-btn"
            >
              Done — Proceed to Bin ({scannedUsns.length} USNs)
            </Button>
          </div>
        )}

        {/* Step: Confirm Bin */}
        {step === 'confirm' && bundleDetails && (
          <div className="flex-1 flex flex-col space-y-4">
            {/* Bundle Info */}
            <div className="bg-gray-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400">Bundle</span>
                <span className="font-mono font-bold text-lg">{bundleCode}</span>
              </div>
              {isRePutaway && (
                <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-3 mb-3">
                  <p className="text-amber-400 text-sm font-semibold">Combo Return</p>
                  <p className="text-amber-300 text-xs">{scannedUsns.length} of {originalUsnCodes.length} USNs returned, {originalUsnCodes.length - scannedUsns.length} consumed</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-700 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">SKU</p>
                  <p className="font-medium truncate">{bundleDetails.sku_code}</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">Qty</p>
                  <p className="font-bold text-xl">{isRePutaway ? scannedUsns.length : bundleDetails.usn_count}</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-3 col-span-2" data-testid="mobile-bundle-fg-name">
                  <p className="text-gray-400 text-xs">FG Name</p>
                  <p className="font-medium text-emerald-300 break-words">{bundleDetails.fg_name || bundleDetails.sku_code || '-'}</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">Class</p>
                  <div className={`inline-flex px-3 py-1 rounded-full text-sm font-bold ${
                    bundleDetails.velocity_class === 'A' ? 'bg-green-500/20 text-green-400' :
                    bundleDetails.velocity_class === 'B' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {bundleDetails.velocity_class || 'C'}
                  </div>
                </div>
              </div>
            </div>

            {/* Multi-staff split preview */}
            {staffList.length > 1 && (
              <div className="bg-gray-800 rounded-2xl p-4" data-testid="sfg-split-preview">
                <p className="text-xs text-gray-400 font-medium mb-2">Split across {staffList.length} staff</p>
                <div className="space-y-1.5">
                  {(() => {
                    const total = isRePutaway ? scannedUsns.length : (bundleDetails.usn_count || 0);
                    const base = Math.floor(total / staffList.length);
                    const rem = total - base * staffList.length;
                    return staffList.map((s, i) => (
                      <div key={s.staff_id} className="flex items-center justify-between text-sm" data-testid={`sfg-split-${s.staff_id}`}>
                        <span className="text-gray-300 truncate">{s.staff_name} <span className="text-gray-500 text-xs">{s.staff_id}</span></span>
                        <span className="font-bold text-purple-300">{base + (i < rem ? 1 : 0)} pcs</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {suggestedBin && (
              <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm text-green-200">Suggested Location</p>
                      <p className="text-2xl font-bold">{suggestedBin.bin_code}</p>
                    </div>
                  </div>
                  {suggestedBin.capacity > 0 && (
                    <div className={`text-right px-3 py-1 rounded-lg ${
                      suggestedBin.available_slots === 0 
                        ? 'bg-red-500/30' 
                        : suggestedBin.available_slots <= 2 
                          ? 'bg-yellow-500/30'
                          : 'bg-white/20'
                    }`}>
                      <p className="text-xs text-green-200">Capacity</p>
                      <p className="font-bold">{suggestedBin.current_stock}/{suggestedBin.capacity}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bin Input */}
            <div className="bg-gray-800 rounded-2xl p-4 space-y-4">
              <div className="text-center">
                <h3 className="font-bold mb-1">Confirm Bin Location</h3>
                <p className="text-sm text-gray-400">Scan bin or use suggested</p>
              </div>
              <Input
                ref={binInputRef}
                value={binCode}
                onChange={(e) => setBinCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleConfirmPutAway)}
                placeholder="Bin Location"
                className="h-14 text-lg text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-bin-input"
              />
            </div>

            {/* Action Buttons */}
            <div className="mt-auto space-y-3">
              <Button
                onClick={handleConfirmPutAway}
                disabled={loading || !binCode}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-confirm-putaway"
              >
                {loading ? 'Processing...' : 'Confirm Put-Away'}
              </Button>
              <Button
                variant="outline"
                onClick={resetForNextBundle}
                className="w-full h-12 border-gray-600 text-gray-300 hover:bg-gray-800 rounded-xl"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Cancel & Rescan
              </Button>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-400">Success!</h2>
                <p className="text-gray-400 mt-2">Bundle stored at {binCode}</p>
              </div>
              <p className="text-sm text-gray-500">Ready for next bundle...</p>
            </div>
          </div>
        )}
      </main>

      {/* Footer Actions */}
      {step !== 'staff' && step !== 'success' && (
        <footer className="bg-gray-800 px-4 py-3 safe-area-inset-bottom">
          <Button
            variant="ghost"
            onClick={resetAll}
            className="w-full text-gray-400 hover:text-white"
          >
            End Session
          </Button>
        </footer>
      )}
    </div>
  );
};

export default MobileSFGPutAway;
